import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiVideo, FiPlay, FiCalendar, FiSquare } from 'react-icons/fi';
import { communicationsAPI } from '../../services/api';
import Button from '../../components/common/Button';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { useNotification } from '../../contexts/NotificationContext';
import { useAuth } from '../../contexts/AuthContext';

const MeetingDetail = () => {
  const { meetingId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showSuccess, showError } = useNotification();
  const [meeting, setMeeting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  const load = useCallback(async () => {
    if (!meetingId) return;
    try {
      setLoading(true);
      const res = await communicationsAPI.getMeeting(meetingId);
      const data = res?.data?.data?.meeting || res?.data?.meeting;
      setMeeting(data);
    } catch (err) {
      showError(err.response?.data?.message || 'Failed to load meeting');
      navigate('/meetings');
    } finally {
      setLoading(false);
    }
  }, [meetingId, navigate, showError]);

  useEffect(() => { load(); }, [load]);

  const handleStartOrJoin = () => {
    navigate(`/meetings/${meetingId}/join`);
  };

  const handleMarkCompleted = async () => {
    try {
      setUpdating(true);
      await communicationsAPI.updateMeetingStatus(meetingId, 'completed');
      showSuccess('Meeting marked as completed');
      load();
    } catch (err) {
      showError(err.response?.data?.message || 'Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  const handleMarkCancelled = async () => {
    if (!window.confirm('Mark this meeting as cancelled?')) return;
    try {
      setUpdating(true);
      await communicationsAPI.updateMeetingStatus(meetingId, 'cancelled');
      showSuccess('Meeting cancelled');
      load();
    } catch (err) {
      showError(err.response?.data?.message || 'Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="page-container flex items-center justify-center min-h-[40vh]">
        <LoadingSpinner />
      </div>
    );
  }

  if (!meeting) {
    return null;
  }

  const isScheduled = meeting.status === 'scheduled';
  const isLive = meeting.status === 'live';
  const isPast = meeting.status === 'completed' || meeting.status === 'cancelled';
  const isSession = meeting.type === 'session';
  const canEndMeeting = !isSession && (user?.role === 'admin' || user?.role === 'teacher');

  return (
    <div className="page-container">
      <div className="mb-4">
        <Button variant="text" size="small" onClick={() => navigate('/meetings')}>
          <FiArrowLeft size={18} className="mr-1" />
          Back to meetings
        </Button>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold">{meeting.title}</h1>
            {meeting.subject && <p className="text-gray-600 mt-0.5 font-medium">Subject: {meeting.subject}</p>}
            <p className="text-gray-600 mt-1">{meeting.description || '—'}</p>
            <div className="flex items-center gap-4 mt-3 text-sm text-gray-500">
              <span className="flex items-center gap-1">
                <FiCalendar size={14} />
                {new Date(meeting.scheduledAt).toLocaleString()}
              </span>
              <span className="capitalize">({(meeting.type || '').replace('_', '–')})</span>
              <span className="px-2 py-0.5 rounded bg-gray-100 capitalize">{meeting.status}</span>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {!isPast && (
              <Button
                variant="primary"
                onClick={handleStartOrJoin}
              >
                {isScheduled ? (
                  <>
                    <FiPlay size={16} className="mr-2" />
                    Start
                  </>
                ) : (
                  <>
                    <FiVideo size={16} className="mr-2" />
                    Join
                  </>
                )}
              </Button>
            )}
            {!isPast && canEndMeeting && (
              <Button
                variant="outline"
                onClick={handleMarkCompleted}
                disabled={updating}
              >
                <FiSquare size={16} className="mr-2" />
                End meeting
              </Button>
            )}
            {!isPast && !isSession && (meeting.createdBy?._id || meeting.createdBy) && (
              <Button
                variant="outline"
                onClick={handleMarkCancelled}
                disabled={updating}
              >
                Cancel meeting
              </Button>
            )}
          </div>
        </div>

        {(meeting.invitees?.length > 0 || meeting.createdBy) && (
          <div className="mt-6 pt-4 border-t">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Details</h3>
            {meeting.createdBy && (
              <p className="text-sm text-gray-600">
                Created by: {meeting.createdBy.firstName} {meeting.createdBy.lastName} {meeting.createdBy.email && `(${meeting.createdBy.email})`}
              </p>
            )}
            {meeting.invitees?.length > 0 && (
              <p className="text-sm text-gray-600 mt-1">
                Invitees: {meeting.invitees.map((u) => `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email).filter(Boolean).join(', ')}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MeetingDetail;
